import type { SyntaxNode } from 'web-tree-sitter';
import type { SymbolTag } from './types.js';
import { createCodeNavYieldState, yieldDuringCodeNavWork } from './yield-during-work.js';

const PARAM_NODE_TYPES = new Set(['required_parameter', 'optional_parameter']);

function tagDedupeKey(tag: SymbolTag): string {
  return `${tag.startLine}:${tag.startColumn}:${tag.endLine}:${tag.endColumn}:${tag.role}:${tag.kind}:${tag.name}`;
}

function collectParamNames(functionTypeNode: SyntaxNode): Set<string> {
  const names = new Set<string>();
  const parameters = functionTypeNode.childForFieldName('parameters');
  if (!parameters) return names;

  function walk(node: SyntaxNode): void {
    if (PARAM_NODE_TYPES.has(node.type)) {
      const nameNode = node.childForFieldName('pattern') ?? node.namedChildren.find((c) => c.type === 'identifier');
      if (nameNode?.type === 'identifier') names.add(nameNode.text);
      return;
    }
    for (const child of node.namedChildren) walk(child);
  }

  walk(parameters);
  return names;
}

function isDefinitionName(node: SyntaxNode): boolean {
  const parent = node.parent;
  if (!parent) return false;
  if (PARAM_NODE_TYPES.has(parent.type)) return true;
  if (parent.type === 'type_parameter' && parent.childForFieldName('name') === node) return true;
  return false;
}

function tagFromIdentifierNode(node: SyntaxNode): SymbolTag {
  return {
    name: node.text,
    role: 'ref',
    kind: 'variable',
    startLine: node.startPosition.row,
    startColumn: node.startPosition.column,
    endLine: node.endPosition.row,
    endColumn: node.endPosition.column,
  };
}

function enclosingFunctionTypes(node: SyntaxNode): SyntaxNode[] {
  const types: SyntaxNode[] = [];
  let current: SyntaxNode | null = node.parent;
  while (current) {
    if (current.type === 'function_type') types.push(current);
    current = current.parent;
  }
  return types;
}

function isFunctionTypeParamReference(node: SyntaxNode): boolean {
  if (node.type !== 'identifier' || isDefinitionName(node)) return false;

  for (const functionType of enclosingFunctionTypes(node)) {
    const returnType = functionType.childForFieldName('return_type');
    if (!returnType?.descendantsOfType('identifier').some((desc) => desc.id === node.id)) continue;
    if (collectParamNames(functionType).has(node.text)) return true;
  }
  return false;
}

/** Capture callback-type parameter references in return types (e.g. type predicates). */
export async function augmentFunctionTypeParamReferenceTags(
  tags: SymbolTag[],
  nameNodes: Map<string, SyntaxNode>,
  root: SyntaxNode,
): Promise<SymbolTag[]> {
  const seen = new Set(tags.map(tagDedupeKey));
  const out = [...tags];
  const state = createCodeNavYieldState();

  async function walk(node: SyntaxNode): Promise<void> {
    if (isFunctionTypeParamReference(node)) {
      const tag = tagFromIdentifierNode(node);
      const key = tagDedupeKey(tag);
      if (!seen.has(key)) {
        seen.add(key);
        out.push(tag);
        nameNodes.set(`${tag.startLine}:${tag.startColumn}:${tag.name}:${tag.role}:${tag.kind}`, node);
      }
    }
    for (const child of node.children) {
      await yieldDuringCodeNavWork(state);
      await walk(child);
    }
  }

  await walk(root);
  return out;
}
